/**
 * Designed  & Developed by Faisal Nadaf
 * Contact: faisalfnadaf@gmail.com
 *
 * @format
 */

import { useEffect, useRef } from "react";
import {
	motion,
	useAnimation,
	useInView,
	useReducedMotion,
} from "motion/react";
import LogoLoop from "./LogoLoop";
import { useGsapScrollParallax } from "@/hooks/useGsapScrollParallax";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

import bhelLogo from "/images/logos/logoslider/bhel.png";
import boschLogo from "/images/logos/logoslider/bosch.png";
import goaShipYardLogo from "/images/logos/logoslider/goaShipYard.png";
import manDieselLogo from "/images/logos/logoslider/manDiesel.png";
import mitsubishiLogo from "/images/logos/logoslider/mitsubishi.png";
import thoshibaLogo from "/images/logos/logoslider/thoshiba.png";

const imageLogos = [
	{ src: bhelLogo, alt: "BHEL" },
	{ src: boschLogo, alt: "Bosch" },
	{ src: goaShipYardLogo, alt: "Goa Shipyard" },
	{ src: manDieselLogo, alt: "MAN Diesel" },
	{ src: mitsubishiLogo, alt: "Mitsubishi" },
	{ src: thoshibaLogo, alt: "Toshiba" },
];

function LogosCrousal() {
	const { t } = useTranslation();
	const controls = useAnimation();
	const ref = useRef<HTMLElement | null>(null);
	const shouldReduceMotion = useReducedMotion();
	const isInView = useInView(ref, { once: true, amount: 0.28 });
	useGsapScrollParallax(ref, { y: -70, opacity: 0.94, scale: 0.99 });

	useEffect(() => {
		if (isInView) {
			controls.start("visible");
		}
	}, [controls, isInView]);

	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				duration: 0.55,
				ease: [0.22, 1, 0.36, 1],
			},
		},
	};

	const buttonVariants = {
		initial: { scale: 1 },
		hover: {
			scale: 1.04,
			transition: {
				type: "spring",
				stiffness: 340,
				damping: 18,
			},
		},
		tap: { scale: 0.98 },
	};
	const itemVariants = {
		hidden: { opacity: 0, y: shouldReduceMotion ? 0 : 18 },
		visible: {
			opacity: 1,
			y: 0,
			transition: {
				duration: 0.55,
				ease: [0.22, 1, 0.36, 1],
			},
		},
	};

	return (
		<motion.section
			ref={ref}
			initial="hidden"
			animate={controls}
			variants={containerVariants}
			className="site-section bg-background">
			<div className="site-container">
				<motion.div
					variants={itemVariants}
					className="mb-8 text-center sm:mb-10">
					<span className="section-kicker justify-center">
						<span className="section-kicker-line" />
						{t("clients.kicker")}
						<span className="section-kicker-line" />
					</span>

					<h2 className="section-title">{t("clients.title")}</h2>
					<p className="section-subtitle mx-auto max-w-2xl">
						{t("clients.subtitle")}
					</p>
				</motion.div>

				<motion.div
					variants={itemVariants}
					className="relative left-1/2 right-1/2 w-screen -translate-x-1/2 overflow-hidden py-6 sm:py-8 lg:py-10">
					{/* Edge fade */}
					<div className="absolute inset-y-0 left-0 z-10 hidden w-24 bg-gradient-to-r from-background to-transparent md:block" />
					<div className="absolute inset-y-0 right-0 z-10 hidden w-24 bg-gradient-to-l from-background to-transparent md:block" />

					<LogoLoop
						logos={imageLogos}
						speed={100}
						direction="left"
						logoHeight={100}
						gap={60}
						pauseOnHover
						fadeOut
						fadeOutColor="var(--background)"
						scaleOnHover
						ariaLabel="Client logos carousel"
					/>
				</motion.div>

				<motion.div
					variants={itemVariants}
					whileHover="hover"
					whileTap="tap"
					initial="initial"
					animate="initial"
					className="mt-6 sm:mt-8 md:mt-10 lg:mt-12 flex justify-center px-4 sm:px-0">
					<Link
						to="/clientele"
						className="w-full sm:w-auto">
						<motion.button
							variants={buttonVariants}
							className="premium-button group w-full sm:w-auto px-6 sm:px-8 md:px-10 py-3 sm:py-4 text-sm sm:text-base">
							{t("clients.cta")}
							<svg
								className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24">
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M14 5l7 7m0 0l-7 7m7-7H3"
								/>
							</svg>
						</motion.button>
					</Link>
				</motion.div>
			</div>
		</motion.section>
	);
}

export default LogosCrousal;
